import React, { createContext, useContext, useState } from 'react';
import { useEncryption } from './EncryptionContext';
import { useJournal } from './JournalContext';
import { JournalEntry } from '../types/journal';

interface SearchContextType {
  searchResults: JournalEntry[];
  searchQuery: string;
  isSearching: boolean;
  filters: {
    tags?: string[];
    moods?: string[];
  };
  setSearchQuery: (query: string) => void;
  setFilters: (filters: { tags?: string[]; moods?: string[] }) => void;
  performSearch: () => Promise<void>;
  clearSearch: () => void;
  searchEntries: (entries: JournalEntry[], query: string) => Promise<void>;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchResults, setSearchResults] = useState<JournalEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState<{ tags?: string[]; moods?: string[] }>({});
  const { decrypt } = useEncryption();
  const { entries } = useJournal();

  const clearSearch = () => {
    setSearchQuery('');
    setFilters({});
    setSearchResults([]);
  };

  const performSearch = async () => {
    setIsSearching(true);
    try {
      await searchEntries(entries, searchQuery);
    } catch (error) {
      console.error('Error during search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const searchEntries = async (entries: JournalEntry[], query: string) => {
    setIsSearching(true);
    setSearchQuery(query);

    try {
      // First filter by tags and moods if they exist
      let filteredEntries = entries;
      
      if (filters.tags?.length) {
        filteredEntries = filteredEntries.filter(entry => 
          entry.tags?.some(tag => filters.tags?.includes(tag))
        );
      }

      if (filters.moods?.length) {
        filteredEntries = filteredEntries.filter(entry =>
          filters.moods?.includes(entry.decrypted_mood_label || '')
        );
      }

      // Then search by content if there's a query
      if (query.trim()) {
        const results = await Promise.all(
          filteredEntries.map(async (entry) => {
            try {
              const decryptedContent = await decrypt(entry.encrypted_content);
              if (decryptedContent.toLowerCase().includes(query.toLowerCase())) {
                return entry;
              }
            } catch (error) {
              console.error('Error decrypting entry during search:', error);
            }
            return null;
          })
        );

        filteredEntries = results.filter((entry): entry is JournalEntry => entry !== null);
      }

      setSearchResults(filteredEntries);
    } catch (error) {
      console.error('Error during search:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <SearchContext.Provider
      value={{
        searchResults,
        searchQuery,
        isSearching,
        filters,
        setSearchQuery,
        setFilters,
        performSearch,
        clearSearch,
        searchEntries,
      }}
    >
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}; 